import { useState, useCallback, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import TextareaAutosize from "react-textarea-autosize";
import { cn } from "src/lib/cn";
import type { CTAActionType } from "./cta-types";
import { GenerateCharacterInput } from "./GenerateCharacterInput";
import { GenerateWorldbuildingInput } from "./GenerateWorldbuildingInput";

type InputProps = {
  onGo: (
    customInstructions: string,
    extra?: {
      role?: string;
      roleLabel?: string;
      fields?: Array<{ id?: string; label: string; value?: unknown }>;
      roleExplicitlySelected?: boolean;
    },
  ) => void;
  onCancel: () => void;
  /** True once this CTA activation's instant fire has already dispatched (survives remounts). */
  instantFired?: boolean;
};

const textareaClassName = cn(
  "w-full resize-none rounded-lg border border-solid border-app-grey-2 bg-app-document-background",
  "px-3 py-2 text-sm text-app-grey-0 placeholder:text-app-grey-1",
  "focus:outline-none focus:border-app-primary-cta",
  "font-[inherit]",
);

const GenerateChapterInput: React.FC<InputProps> = ({ onGo, onCancel }) => {
  const [instructions, setInstructions] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setTimeout(() => textareaRef.current?.focus(), 300);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        onGo(instructions.trim());
      }
    },
    [instructions, onGo],
  );

  return (
    <>
      <TextareaAutosize
        ref={textareaRef}
        value={instructions}
        onChange={(e) => setInstructions(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Tell Sudowrite how to write this (optional)..."
        className={textareaClassName}
        minRows={2}
        maxRows={10}
      />
      <div className="flex items-center justify-between pt-1">
        <button
          onClick={onCancel}
          className={cn(
            "bg-transparent border-0 cursor-pointer",
            "text-app-grey-1 hover:text-app-grey-0 transition-colors",
            "text-sm font-medium px-2 py-1",
          )}
        >
          Cancel
        </button>
        <button
          onClick={() => onGo(instructions.trim())}
          className={cn(
            "flex items-center gap-1.5 rounded-lg px-3 py-1.5",
            "bg-app-primary-cta text-white border-0 cursor-pointer",
            "hover:brightness-110 active:scale-[0.98] transition-all",
            "text-sm font-semibold",
          )}
        >
          Go!
          <span className="flex items-center gap-0.5 opacity-70 text-xs">
            <kbd className="font-sans">⌘</kbd>
            <kbd className="font-sans">↵</kbd>
          </span>
        </button>
      </div>
    </>
  );
};

const RewriteOutlineInput: React.FC<InputProps> = ({ onGo, onCancel }) => {
  const [instructions, setInstructions] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const canSubmit = instructions.trim().length > 0;

  useEffect(() => {
    setTimeout(() => textareaRef.current?.focus(), 300);
  }, []);

  const handleSubmit = useCallback(() => {
    const trimmed = instructions.trim();
    if (!trimmed) return;
    onGo(trimmed);
  }, [instructions, onGo]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit],
  );

  return (
    <>
      <p className="text-sm text-app-grey-1 m-0">Instructions are required.</p>
      <TextareaAutosize
        ref={textareaRef}
        value={instructions}
        onChange={(e) => setInstructions(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Tell Sudowrite how to rewrite your outline..."
        className={textareaClassName}
        minRows={2}
        maxRows={10}
      />
      <div className="flex items-center justify-between pt-1">
        <button
          onClick={onCancel}
          className={cn(
            "bg-transparent border-0 cursor-pointer",
            "text-app-grey-1 hover:text-app-grey-0 transition-colors",
            "text-sm font-medium px-2 py-1",
          )}
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={!canSubmit}
          className={cn(
            "flex items-center gap-1.5 rounded-lg px-3 py-1.5",
            "bg-app-primary-cta text-white border-0",
            "text-sm font-semibold transition-all",
            canSubmit
              ? "cursor-pointer hover:brightness-110 active:scale-[0.98]"
              : "cursor-not-allowed opacity-30 hover:brightness-100 active:scale-100",
          )}
        >
          Go!
          <span className="flex items-center gap-0.5 opacity-70 text-xs">
            <kbd className="font-sans">⌘</kbd>
            <kbd className="font-sans">↵</kbd>
          </span>
        </button>
      </div>
    </>
  );
};

const InstantFireInput: React.FC<InputProps> = ({ onGo, instantFired }) => {
  // hasFiredRef only guards within one mount; instantFired (persisted on the CTA action)
  // is what prevents a re-send when this component remounts mid-activation.
  const hasFiredRef = useRef(false);

  useEffect(() => {
    if (hasFiredRef.current || instantFired) return;
    hasFiredRef.current = true;
    onGo("");
  }, [onGo, instantFired]);

  return null;
};

const inputByType: Partial<Record<CTAActionType, React.FC<InputProps>>> = {
  generate_outline: GenerateChapterInput,
  rewrite_outline: RewriteOutlineInput,
  generate_chapter: GenerateChapterInput,
  generate_trait: GenerateChapterInput,
  rewrite_trait: GenerateChapterInput,
  generate_worldbuilding_trait: GenerateChapterInput,
  rewrite_worldbuilding_trait: GenerateChapterInput,
  generate_synopsis: InstantFireInput,
  rewrite_synopsis: GenerateChapterInput,
  generate_character: GenerateCharacterInput,
  generate_worldbuilding: GenerateWorldbuildingInput,
  generate_cast: InstantFireInput,
};

type CTAInputBodyProps = {
  actionType: CTAActionType;
  onGo: (
    customInstructions: string,
    extra?: {
      role?: string;
      roleLabel?: string;
      fields?: Array<{ id?: string; label: string; value?: unknown }>;
      roleExplicitlySelected?: boolean;
    },
  ) => void;
  onCancel: () => void;
  instantFired?: boolean;
};

export const CTAInputBody: React.FC<CTAInputBodyProps> = ({ actionType, onGo, onCancel, instantFired }) => {
  const InputComponent = inputByType[actionType] || GenerateChapterInput;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
      className="px-3 pb-3 flex flex-col gap-2"
    >
      <InputComponent
        key={actionType}
        onGo={onGo}
        onCancel={onCancel}
        instantFired={instantFired}
      />
    </motion.div>
  );
};
